import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormikContext } from 'formik';
import {
  FormProvider,
  NavProvider,
  Screen,
  useStateNavigator,
  SuperSchema,
  CustomComponentProps,
} from '@updater/ui-informant';
import {
  Alert,
  Box,
  CodeInput,
  Flex,
  RadioList,
  TextLink,
} from '@updater/ui-uds';
import { TrackEventInput } from '@updater/ui-tracker';
import { useTracking } from 'react-tracking';
import { AuthStackedTemplate } from './AuthStackedTemplate';
import { MovingAppBrandedHeader } from 'components/branded-header';
import { useAuth } from 'context';
import { useLoginStore } from 'flows/core/store';
import { EMAIL_REGX } from 'constants/regx';

import { useLoginMethods } from './hooks/useLoginMethods';
import { useGenerateSigninOtp } from './hooks/useGenerateSigninOtp';
import { useSigninWithOtp } from './hooks/useSigninWithOtp';

const schema: SuperSchema = {
  type: 'object',
  required: ['email'],
  properties: {
    email: {
      title: 'email',
      type: 'string',
      pattern: EMAIL_REGX,
      ui: {
        label: 'Email',
        inputType: 'email',
        messages: {
          required: 'Please enter your email address',
          pattern: 'Please enter a valid email address',
        },
      },
    },
    authMethod: {
      type: 'string',
      ui: {
        component: 'AuthMethodSelect',
        componentProps: {
          controlSize: 'l',
          direction: 'vertical',
          distributeEqually: true,
        },
      },
    },
    password: {
      title: 'password',
      type: 'string',
      ui: {
        label: 'Password',
        component: 'security',
        messages: {
          required: 'Please enter a password',
        },
      },
    },
    otpCode: {
      title: 'otpCode',
      type: 'string',
      minLength: 6,
      maxLength: 6,
      ui: {
        component: 'OtpCodeInput',
        messages: {
          required: 'Please enter the verification code',
          minLength: 'Please enter all 6 digits',
        },
      },
    },
  },
};

type FormData = {
  email: string;
  authMethod: string;
  password: string;
  otpCode: string;
};

const AuthMethodSelect = ({
  value,
  onChange,
  schema: fieldSchema,
}: CustomComponentProps) => {
  const { values } = useFormikContext<FormData>();
  const { componentProps } = fieldSchema.ui ?? {};

  const options = [
    { value: 'password', label: 'Continue with password' },
    { value: 'otp', label: `Send an OTP to ${values.email}` },
  ];

  return (
    <RadioList
      value={value}
      onChange={onChange}
      options={options}
      direction={componentProps?.direction ?? 'vertical'}
      controlSize={componentProps?.controlSize ?? 'l'}
      distributeEqually={componentProps?.distributeEqually ?? true}
    />
  );
};

const OtpCodeInput = ({ onChange, error }: CustomComponentProps) => (
  <CodeInput length={6} error={error} onChange={onChange} />
);

interface Props {
  title?: string;
}

export const AuthOverlayOtp = ({ title }: Props) => {
  const { login, error: loginError, setSession } = useAuth();
  const { email } = useLoginStore();
  const navigator = useStateNavigator('');
  const { trackEvent } = useTracking<TrackEventInput<unknown>>({
    domain: 'auth',
  });

  const [hasPassword, setHasPassword] = useState(false);

  const navigationOverride = useRef<string | null>(null);
  const wrappedNavigator = useMemo(
    () => ({
      navigate: (path: string) => {
        if (navigationOverride.current !== null) {
          const override = navigationOverride.current;
          navigationOverride.current = null;
          return navigator.navigate(override, { replace: false });
        }
        return navigator.navigate(path, { replace: false });
      },
      getCurrentPath: () => navigator.getCurrentPath(),
    }),
    [navigator]
  );

  const currentPath = navigator.getCurrentPath();
  const [submittedEmail, setSubmittedEmail] = useState('');

  const [methodsSkipped, setMethodsSkipped] = useState(false);

  const loginMethods = useLoginMethods();
  const generateOtp = useGenerateSigninOtp();
  const signinOtp = useSigninWithOtp();

  useEffect(() => {
    if (currentPath !== '/verify' || !submittedEmail) return;
    signinOtp.clearError();
    void generateOtp.generate(submittedEmail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath, submittedEmail]);

  const handleResendOtp = async () => {
    if (!submittedEmail) return;
    const success = await generateOtp.resend(submittedEmail);
    if (success) signinOtp.clearError();
  };

  const handleSubmit = async (values: FormData) => {
    const result = await signinOtp.signin(values.email, values.otpCode);
    if (!result) return;
    setSession({
      userId: result.mover.id,
      userUUID: result.mover.uuid,
      session: {
        accessToken: result.session.accessToken,
        client: result.session.client,
        uid: result.session.uid,
      },
    });
    trackEvent({
      object: 'passwordless-login',
      verb: 'submitted',
      details: { loginMethod: 'otp', hasPassword },
    });
  };

  return (
    <Flex flexDirection="column" minHeight="100vh">
      <MovingAppBrandedHeader />
      <Flex
        flex={1}
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        px="s"
      >
        <Flex flexDirection="column" width="100%" maxWidth="490px">
          <FormProvider
            schema={schema}
            initialValues={{
              email: email ?? '',
              authMethod: '',
              password: '',
              otpCode: '',
            }}
            onSubmit={handleSubmit}
            customComponents={{ OtpCodeInput, AuthMethodSelect }}
          >
            <NavProvider
              template={AuthStackedTemplate}
              customLatestScreenIdx={3}
              onBeforeNavigate={async ({
                fromScreen,
                fromIndex,
                toIndex,
                formValues,
              }) => {
                // email screen forward
                if (fromScreen?.name === 'emailScreen' && toIndex > fromIndex) {
                  setMethodsSkipped(false);

                  const methods = await loginMethods.fetch(formValues.email);
                  if (!methods) {
                    navigationOverride.current = '/';
                    return;
                  }

                  setSubmittedEmail(formValues.email);

                  const { password, emailOtp } = methods;
                  setHasPassword(password.enabled);

                  // only password available
                  if (password.enabled && !emailOtp.enabled) {
                    setMethodsSkipped(true);
                    navigationOverride.current = '/password';
                    return;
                  }

                  // only otp available
                  if (emailOtp.enabled && !password.enabled) {
                    setMethodsSkipped(true);
                    navigationOverride.current = '/verify';
                    return;
                  }
                }

                // method screen back
                if (
                  fromScreen?.name === 'methodScreen' &&
                  toIndex < fromIndex
                ) {
                  loginMethods.clearError();
                  return;
                }

                // method screen forward
                if (
                  fromScreen?.name === 'methodScreen' &&
                  formValues.authMethod === 'otp' &&
                  toIndex > fromIndex
                ) {
                  navigationOverride.current = '/verify';
                  return;
                }

                // otp screen back
                if (fromScreen?.name === 'otpScreen' && toIndex < fromIndex) {
                  generateOtp.reset();
                  signinOtp.clearError();
                  const target = methodsSkipped ? '/' : '/method';
                  if (target === '/') loginMethods.clearError();
                  navigationOverride.current = target;
                  return;
                }

                // password screen back
                if (
                  fromScreen?.name === 'passwordScreen' &&
                  toIndex < fromIndex &&
                  methodsSkipped
                ) {
                  loginMethods.clearError();
                  navigationOverride.current = '/';
                  return;
                }

                // password screen forward
                if (
                  fromScreen?.name === 'passwordScreen' &&
                  toIndex > fromIndex
                ) {
                  navigationOverride.current = '/password';
                  await login(formValues.email, formValues.password);
                  trackEvent({
                    object: 'passwordless-login',
                    verb: 'submitted',
                    details: { loginMethod: 'password', hasPassword },
                  });
                  return;
                }
              }}
              navigator={wrappedNavigator}
            >
              <Screen
                name="emailScreen"
                heading={title ?? 'Welcome back!'}
                subHeading="Enter your email to continue."
                route=""
                fields={['email']}
                hideBackButton
                templateProps={{
                  footerPanel: loginMethods.error && (
                    <Alert
                      message={loginMethods.error}
                      variant="error"
                      mt="s"
                    />
                  ),
                }}
              />
              <Screen
                name="methodScreen"
                heading="How would you like to sign in?"
                route="method"
                fields={['authMethod']}
              />
              <Screen
                name="passwordScreen"
                heading="Enter your password"
                route="password"
                fields={['password']}
                nextButtonText="Log In"
                templateProps={{
                  footerPanel: loginError && (
                    <Alert
                      message="Your password was incorrect. Please try again."
                      variant="error"
                      mt="s"
                    />
                  ),
                }}
              />
              <Screen
                name="otpScreen"
                heading="Check your email"
                subHeading="We sent a 6-digit code to your email. It may take a few minutes to arrive."
                route="verify"
                fields={['otpCode']}
                nextButtonText="Verify"
                templateProps={{
                  footerPanel: (
                    <Box>
                      <TextLink
                        data-testid="resend-otp"
                        variant="m"
                        showTextDecoration
                        onClick={handleResendOtp}
                      >
                        {generateOtp.resendLoading ? 'Sending…' : 'Resend code'}
                      </TextLink>
                      {(generateOtp.error || signinOtp.error) && (
                        <Alert
                          message={generateOtp.error ?? signinOtp.error ?? ''}
                          variant="error"
                          mt="s"
                        />
                      )}
                      {generateOtp.successMessage && (
                        <Alert
                          message={generateOtp.successMessage}
                          variant="success"
                          mt="s"
                        />
                      )}
                    </Box>
                  ),
                }}
              />
            </NavProvider>
          </FormProvider>
        </Flex>
      </Flex>
    </Flex>
  );
};
